/**
 * AUD-01: Bitácora de actividad del tenant.
 * Muestra el historial de cambios (create/update/delete) de los modelos clave.
 */
import { useState, useEffect } from 'react'
import {
  Shield, Search, SlidersHorizontal, X,
  Clock, User, Monitor, Eye, Pencil, Trash2, RefreshCw, Cpu,
} from 'lucide-react'
import {
  useAuditLog, useAuditLogMeta, useLocalizationConfig, useUpdateLocalizationConfig,
  type AuditLogFilters, type AuditLogEntry,
} from '../hooks/useConfig'
import { inputCls, labelCls } from '@/lib/formStyles'
import DatePicker from '@/components/DatePicker'
import { Pagination } from '@/components/ds/Pagination'
import { useTenantLocale } from '@/lib/locale'
import { SelectField } from '@/components/ds/SelectField'

// ─── Colores de acción ────────────────────────────────────────────────────────

const ACTION_STYLES: Record<number, { label: string; cls: string; Icon: React.ElementType }> = {
  0: { label: 'Creación',      cls: 'bg-success-surface-strong text-success-content',   Icon: Eye },
  1: { label: 'Actualización', cls: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',     Icon: Pencil },
  2: { label: 'Eliminación',   cls: 'bg-danger-surface-strong text-danger-content',       Icon: Trash2 },
}

// ─── Formato de fecha ─────────────────────────────────────────────────────────

function formatDate(ts: string) {
  const d = new Date(ts)
  return d.toLocaleString('es-CL', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

// ─── Diff de cambios ──────────────────────────────────────────────────────────

function ChangesDiff({ changes }: { changes: AuditLogEntry['changes'] }) {
  if (!changes || Object.keys(changes).length === 0) return null

  return (
    <div className="mt-2 space-y-1">
      {Object.entries(changes).map(([field, vals]) => {
        const [before, after] = Array.isArray(vals) ? vals : [null, vals]
        return (
          <div key={field} className="flex flex-wrap gap-1 text-xs font-mono">
            <span className="text-content-muted font-sans font-medium">{field}:</span>
            {before !== null && before !== '' && (
              <span className="line-through text-danger-solid bg-danger-surface px-1 rounded max-w-[200px] truncate">
                {String(before ?? '—')}
              </span>
            )}
            <span className="text-success-content bg-success-surface px-1 rounded max-w-[200px] truncate">
              {String(after ?? '—')}
            </span>
          </div>
        )
      })}
    </div>
  )
}

// ─── Fila de entrada ──────────────────────────────────────────────────────────

function EntryRow({ entry, expanded, onToggle }: {
  entry: AuditLogEntry
  expanded: boolean
  onToggle: () => void
}) {
  const action = ACTION_STYLES[entry.action_code] ?? { label: entry.action, cls: 'bg-surface-muted text-content-muted', Icon: Eye }
  const Icon = action.Icon
  const hasChanges = entry.changes && Object.keys(entry.changes).length > 0

  return (
    <div
      className={`border rounded-lg px-4 py-3 cursor-pointer transition-colors ${
        expanded ? 'bg-surface-muted border-primary-200' : 'bg-surface hover:bg-soft border-divider'
      }`}
      onClick={onToggle}
    >
      <div className="flex items-start gap-3">
        {/* Ícono acción */}
        <div className={`mt-0.5 flex-shrink-0 rounded-full p-1.5 ${action.cls}`}>
          <Icon size={12} />
        </div>

        {/* Info principal */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-sm text-content">{entry.object_repr}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${action.cls}`}>
              {action.label}
            </span>
            <span className="text-xs text-content-muted bg-surface-muted px-2 py-0.5 rounded-full">
              {entry.module} · {entry.model}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-content-muted">
            <span className="flex items-center gap-1">
              <Clock size={11} />
              {formatDate(entry.timestamp)}
            </span>
            {entry.actor ? (
              <span className="flex items-center gap-1">
                <User size={11} />
                {entry.actor.name || entry.actor.email || `ID ${entry.actor.id}`}
              </span>
            ) : (
              <span
                className="flex items-center gap-1 text-content-subtle"
                title="Cambio originado por un proceso automático del sistema (deploy, migración, tarea programada o carga masiva). No tiene actor humano asociado."
              >
                <Cpu size={11} />
                Sistema (proceso automático)
              </span>
            )}
            {entry.remote_addr && (
              <span className="flex items-center gap-1">
                <Monitor size={11} />
                {entry.remote_addr}
              </span>
            )}
          </div>

          {expanded && hasChanges && <ChangesDiff changes={entry.changes} />}
          {expanded && !hasChanges && (
            <p className="mt-2 text-xs text-content-subtle italic">Sin detalle de cambios registrado.</p>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Drawer de filtros ────────────────────────────────────────────────────────

function FiltersDrawer({ open, onClose, filters, setFilter, meta, onClear }: {
  open: boolean
  onClose: () => void
  filters: AuditLogFilters
  setFilter: (key: keyof AuditLogFilters, val: string | number | undefined) => void
  meta: ReturnType<typeof useAuditLogMeta>['data']
  onClear: () => void
}) {
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity duration-200
          ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />
      <div
        className={`fixed inset-y-0 right-0 z-50 w-80 bg-surface shadow-2xl border-l border-divider
          transform transition-transform duration-200 ease-out
          ${open ? 'translate-x-0' : 'translate-x-full'}`}
        style={{ backgroundColor: 'rgb(var(--color-surface))' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-divider">
          <div className="flex items-center gap-2">
            <SlidersHorizontal size={16} className="text-content-muted" />
            <h3 className="text-sm font-semibold text-content">Filtros avanzados</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-soft text-content-subtle transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 130px)' }}>
          <SelectField
            dense
            label="Módulo"
            value={filters.module ?? ''}
            onChange={e => setFilter('module', e.target.value || undefined)}
          >
            <option value="">Todos</option>
            {meta?.modules.map(m => (
            <option key={m.code} value={m.code}>{m.label}</option>
            ))}
          </SelectField>

          <SelectField
            dense
            label="Acción"
            value={filters.action ?? ''}
            onChange={e => setFilter('action', e.target.value || undefined)}
          >
            <option value="">Todas</option>
            {meta?.actions.map(a => (
            <option key={a.code} value={String(a.code)}>{a.label}</option>
            ))}
          </SelectField>

          <div>
            <label htmlFor="auditlogtab-desde" className={labelCls}>Desde</label>
            <DatePicker id="auditlogtab-desde"
              value={filters.date_from ?? ''}
              onChange={v => setFilter('date_from', v || undefined)}
              className={inputCls}
            />
          </div>

          <div>
            <label htmlFor="auditlogtab-hasta" className={labelCls}>Hasta</label>
            <DatePicker id="auditlogtab-hasta"
              value={filters.date_to ?? ''}
              onChange={v => setFilter('date_to', v || undefined)}
              className={inputCls}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-divider bg-surface">
          <div className="flex gap-2">
            <button
              onClick={onClear}
              className="flex-1 px-3 py-2 text-xs font-medium text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors"
            >
              Limpiar filtros
            </button>
            <button
              onClick={onClose}
              className="flex-1 px-3 py-2 text-xs font-medium text-on-primary bg-primary rounded-lg hover:bg-primary-hover transition-colors"
            >
              Aplicar
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

// ─── Configuración de retención ───────────────────────────────────────────────

function RetentionConfig() {
  const { data: cfg } = useLocalizationConfig()
  const { mutate: update, isPending } = useUpdateLocalizationConfig()
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState<string>('')

  useEffect(() => {
    if (cfg?.audit_log_retention_days != null) {
      setValue(String(cfg.audit_log_retention_days))
    }
  }, [cfg?.audit_log_retention_days])

  const current = cfg?.audit_log_retention_days ?? 365

  function save() {
    const days = parseInt(value, 10)
    if (!Number.isFinite(days) || days < 30 || days > 3650) return
    update({ audit_log_retention_days: days } as any, {
      onSuccess: () => setEditing(false),
    })
  }

  if (!cfg) return null

  return (
    <div className="flex items-center gap-2 text-xs text-content-muted">
      <Clock size={12} />
      <span>Retención:</span>
      {editing ? (
        <>
          <input aria-label="Días de retención"
            type="number"
            min={30}
            max={3650}
            value={value}
            onChange={e => setValue(e.target.value)}
            className="w-20 px-2 py-1 rounded border border-divider bg-surface text-content focus:outline-none focus:ring-2 focus:ring-focus-ring"
          />
          <span>días</span>
          <button onClick={save} disabled={isPending} className="text-primary-700 hover:underline disabled:opacity-50">
            Guardar
          </button>
          <button onClick={() => { setEditing(false); setValue(String(current)) }} className="text-content-muted hover:underline">
            Cancelar
          </button>
        </>
      ) : (
        <>
          <span className="font-medium text-content">{current} días</span>
          <button
            onClick={() => setEditing(true)}
            className="text-primary-700 hover:underline"
            title="Los registros más antiguos se eliminan automáticamente el primer día de cada mes."
          >
            Cambiar
          </button>
        </>
      )}
    </div>
  )
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function AuditLogTab() {
  const locale = useTenantLocale()
  const [filters, setFilters] = useState<AuditLogFilters>({ page: 1, page_size: 50 })
  const [expanded, setExpanded] = useState<number | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const { data, isLoading, refetch, isFetching } = useAuditLog(filters)
  const { data: meta } = useAuditLogMeta()

  function setFilter(key: keyof AuditLogFilters, val: string | number | undefined) {
    setFilters(f => ({ ...f, [key]: val, page: 1 }))
  }

  function handleSearch(e: React.ChangeEvent<HTMLInputElement>) {
    setFilter('search', e.target.value || undefined)
  }

  function goPage(p: number) {
    setFilters(f => ({ ...f, page: p }))
  }

  function clearFilters() {
    setFilters(f => ({ page: 1, page_size: f.page_size, search: f.search }))
  }

  const totalPages = data?.total_pages ?? 1
  const currentPage = filters.page ?? 1
  const hasAdvancedFilters = !!(filters.module || filters.action || filters.date_from || filters.date_to)

  return (
    <div className="space-y-4">
      {/* Barra búsqueda + botones */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-subtle" />
          <input aria-label="Buscar por objeto (factura, cliente, producto…)"
            type="text"
            placeholder="Buscar por objeto (factura, cliente, producto…)"
            className={`${inputCls} pl-9`}
            onChange={handleSearch}
          />
        </div>
        <button
          onClick={() => setDrawerOpen(true)}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-colors ${
            hasAdvancedFilters
              ? 'bg-primary-50 border-primary-300 text-primary-700'
              : 'border-divider text-content-muted hover:bg-soft'
          }`}
        >
          <SlidersHorizontal size={14} />
          Filtros
          {hasAdvancedFilters && (
            <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
          )}
        </button>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-divider text-content-muted hover:bg-soft disabled:opacity-50 transition-colors"
        >
          <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Contador + retención */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {data && (
          <p className="text-xs text-content-muted">
            {data.count.toLocaleString('es-CL')} registro{data.count !== 1 ? 's' : ''}
            {currentPage > 1 ? ` · Página ${currentPage} de ${totalPages}` : ''}
          </p>
        )}
        <RetentionConfig />
      </div>

      {/* Lista */}
      {isLoading ? (
        <div className="space-y-2">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-14 bg-surface-muted rounded-lg animate-pulse" />
          ))}
        </div>
      ) : data?.results.length === 0 ? (
        <div className="text-center py-12 text-content-subtle">
          <Shield size={32} className="mx-auto mb-2 opacity-30" />
          <p>No hay registros con los filtros actuales.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {data?.results.map(entry => (
            <EntryRow
              key={entry.id}
              entry={entry}
              expanded={expanded === entry.id}
              onToggle={() => setExpanded(expanded === entry.id ? null : entry.id)}
            />
          ))}
        </div>
      )}

      {/* Paginación */}
      {totalPages > 1 && (
        <Pagination
          page={currentPage}
          pageSize={filters.page_size ?? 50}
          total={data?.count ?? 0}
          onPageChange={goPage}
          locale={locale}
        />
      )}

      {/* Drawer de filtros */}
      <FiltersDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        filters={filters}
        setFilter={setFilter}
        meta={meta}
        onClear={clearFilters}
      />
    </div>
  )
}
